import { navigate } from 'gatsby'
import React from 'react'
import { Field, Form } from 'react-final-form'

import { Alert } from '@cinch-labs/design-system'
import { Primary, Tertiary } from '@cinch/oxd-components/button/src'
import Panel, { PanelLevel } from '@cinch/oxd-components/panel/src'
import { Text, TextStyles } from '@cinch-labs/design-system'
import submitVrm from '@cinch/oxd-pages/wbac/src/stages/landing-page/submit-vrm'
import { parseNumberPlate, validateNumberPlate } from '@cinch/oxd-utils/validators/src'

import * as styles from './sell-input-form.module.css'
import { TextFieldWrapper } from '../../final-form-wrappers/src'

interface SellCarProps {
  i18n: {
    title: string
    body: string
    numberPlate: {
      label: string
      placeholder: string
    }
    ctaText: string
    ctaLabel: string
  }
  isRetry?: boolean
  vrm?: string
}

const SellInputForm: React.FC<SellCarProps> = ({ i18n, isRetry, vrm = '' }) => {
  const initialValues = { vrm }
  const goToVehicleConfirm = (formData: any) => submitVrm(formData.vrm, navigate)
  const goToManualLookup = () => navigate(`/sell/manual-lookup/${vrm}`)
  const retryError = 'We can’t find a match. Try again or enter your details manually.'

  return (
    <Panel level={PanelLevel.top} title={i18n.title} subtitle={i18n.body}>
      <div className={styles.body} data-testid="vrm-form">
        <Form
          initialValues={initialValues}
          onSubmit={goToVehicleConfirm}
          render={({ handleSubmit, submitting, submitError, values }) => {
            const isBtnDisabled = !!validateNumberPlate(values.vrm)

            return (
              <form onSubmit={handleSubmit}>
                {(submitError || isRetry) && (
                  <div className={styles.alert}>
                    <Alert oxford={true}>{submitError || retryError}</Alert>
                  </div>
                )}

                <div className={styles.form}>
                  <Field
                    fullWidth={true}
                    name="vrm"
                    component={TextFieldWrapper}
                    oxdOverride={true}
                    oxford={true}
                    parse={parseNumberPlate}
                    validate={validateNumberPlate}
                    label={i18n.numberPlate.label}
                    placeholder={i18n.numberPlate.placeholder}
                    data-testid="vrm-input"
                  />

                  {isRetry && (
                    <Tertiary
                      className={styles.tertiaryBtn}
                      onClick={goToManualLookup}
                      type="button"
                      data-testid="manual-button"
                    >
                      Enter Details Manually
                    </Tertiary>
                  )}

                  <div className={styles.textContainer}>
                    <Text
                      oxford={true}
                      type={TextStyles.caption}
                      element="p"
                      dangerouslySetInnerHTML={{ __html: i18n.ctaText }}
                    />
                  </div>

                  <Primary
                    oxdClassName={styles.primaryBtn}
                    data-field="submit"
                    type="submit"
                    disabled={isBtnDisabled}
                    isLoading={submitting}
                    data-testid="submit-button"
                    oxford={true}
                  >
                    {i18n.ctaLabel}
                  </Primary>
                </div>
              </form>
            )
          }}
        />
      </div>
    </Panel>
  )
}

export default SellInputForm
